import { RefObject } from 'react'

import {
  fadeInDurationMs,
  fadeOutDurationMs,
  grad,
  grid,
  lineColor,
  maxDurationMs,
  maxProjectileLen,
  maxSteps,
  minDurationMs,
  minProjectileLen,
  minSteps,
  projectileCount,
} from './constants'

type Projectile = Record<string, number>

export class GridAnimationBackground {
  private ctx: CanvasRenderingContext2D | null
  private canvas: HTMLCanvasElement | null
  private width: number
  private height: number
  private rcount: number
  private ccount: number
  private hprojectiles: Projectile[]
  private vprojectiles: Projectile[]
  private observer: ResizeObserver | undefined

  constructor() {
    const canvas = document.createElement('canvas')

    this.canvas = canvas
    this.ctx = canvas.getContext('2d')
    this.width = 0
    this.height = 0
    this.rcount = 0
    this.ccount = 0
    this.hprojectiles = []
    this.vprojectiles = []
  }

  unmount() {
    this.observer?.unobserve(this.canvas?.parentElement!)
    this.observer?.disconnect()
    this.observer = undefined

    this.canvas = null
    this.ctx = null
    this.width = 0
    this.height = 0
    this.rcount = 0
    this.ccount = 0
    this.hprojectiles = []

    this.vprojectiles = []
  }

  addAnimationByRef(ref: RefObject<any>) {
    ref.current?.appendChild(this.canvas)
  }

  main() {
    const rect = this.canvas?.parentElement?.getBoundingClientRect()
    this.width = rect?.width ?? 0
    this.height = rect?.height ?? 0
    if (this.canvas) {
      this.canvas.width = this.width
      this.canvas.height = this.height
    }

    this.observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (!entry) {
        return
      }
      const rect = entry.target.getBoundingClientRect()
      this.width = rect.width
      this.height = rect.height
      if (this.canvas) {
        this.canvas.width = this.width
        this.canvas.height = this.height
      }
    })
    this.observer.observe(this.canvas?.parentElement!)

    this.rcount = this.getrcount()
    for (let i = 0; i < projectileCount; ++i) {
      this.hprojectiles.push(this.createProjectile(this.rcount, this.width))
    }
    this.ccount = this.getccount()
    for (let i = 0; i < projectileCount; ++i) {
      this.vprojectiles.push(this.createProjectile(this.ccount, this.height))
    }

    requestAnimationFrame(this.draw.bind(this))
  }

  draw(time: number) {
    this.ctx?.clearRect(0, 0, this.width, this.height)

    this.drawHorizontalLines()
    this.drawVerticalLines()

    for (const projectile of this.hprojectiles) {
      this.drawHProjectile(projectile, time)
    }

    for (const projectile of this.vprojectiles) {
      this.drawVProjectile(projectile, time)
    }

    for (let i = 0; i < this.hprojectiles.length; ++i) {
      const projectile = this.hprojectiles[i]
      if (projectile.startTime + projectile.duration <= time) {
        this.hprojectiles[i] = this.createProjectile(this.rcount, this.width, time)
      }
    }

    for (let i = 0; i < this.vprojectiles.length; ++i) {
      const projectile = this.vprojectiles[i]
      if (projectile.startTime + projectile.duration <= time) {
        this.vprojectiles[i] = this.createProjectile(this.ccount, this.height, time)
      }
    }

    requestAnimationFrame(this.draw.bind(this))
  }

  createProjectile(count: number, span: number, currentTime?: number) {
    const len = this.rand(minProjectileLen, maxProjectileLen)
    const speed = this.rand(minSteps, maxSteps)
    const startTime = (currentTime || document.timeline.currentTime) ?? 0
    const duration = this.rand(minDurationMs, maxDurationMs)
    const row = Math.floor(Math.random() * count) % count
    const startPos = Math.floor(Math.random() * span) % span
    const dir = Math.random() < 0.5 ? -1 : 1
    return { startTime, duration, row, len, startPos, dir, speed }
  }

  withStep(projectile: Projectile, span: number, time: number) {
    const { len, speed, startPos, dir } = projectile
    const total = span + len
    const step = ((time % speed) / speed) * total
    return ((startPos + step * dir + total) % total) - len
  }

  getOpacity(projectile: Projectile, time: number) {
    const { startTime, duration } = projectile
    let opacity = 1
    const toTime = startTime + duration - time
    if (toTime < fadeOutDurationMs) {
      opacity = toTime > 0 ? toTime / fadeOutDurationMs : 0
    }
    const fromTime = time - startTime
    if (fromTime < fadeInDurationMs) {
      opacity = fromTime > 0 ? fromTime / fadeInDurationMs : 0
    }
    return opacity
  }

  drawHProjectile(projectile: Projectile, time: number) {
    this.ctx?.save()

    const { len, dir } = projectile
    const x = this.withStep(projectile, this.width, time)
    const y = this.gety(projectile.row)

    const [start, end] = this.adjustForDir(dir, x, len)
    const opacity = this.getOpacity(projectile, time)
    if (this.ctx) {
      this.ctx.fillStyle = this.createLineGradient(start, y, end, y, opacity)!
    }
    this.ctx?.fillRect(x, y, len, 1)
    this.ctx?.restore()
  }

  drawVProjectile(projectile: Projectile, time: number) {
    this.ctx?.save()

    const { len, dir } = projectile
    const x = this.getx(projectile.row)
    const y = this.withStep(projectile, this.height, time)

    const [start, end] = this.adjustForDir(dir, y, len)
    const opacity = this.getOpacity(projectile, time)
    if (this.ctx) {
      this.ctx.fillStyle = this.createLineGradient(x, start, x, end, opacity)!
    }
    this.ctx?.fillRect(x, y, 1, len)
    this.ctx?.restore()
  }

  adjustForDir(dir: number, start: number, len: number) {
    const res = [start, start + len]
    if (dir < 0) {
      res.reverse()
    }
    return res
  }

  createLineGradient(x0: number, y0: number, x1: number, y1: number, opacity: number) {
    const gradient = this.ctx?.createLinearGradient(x0, y0, x1, y1)
    grad.forEach(([s, r, g, b, _a]) => {
      const a = _a * opacity
      gradient?.addColorStop(s, `rgba(${r}, ${g}, ${b}, ${a})`)
    })
    return gradient
  }

  drawHorizontalLines() {
    let y = this.gety(0)
    this.ctx?.save()
    if (this.ctx) {
      this.ctx.fillStyle = lineColor
    }
    while (y < this.height) {
      this.ctx?.fillRect(0, y, this.width, 1)
      y += grid.dy
    }
    this.ctx?.restore()
  }

  drawVerticalLines() {
    let x = this.getx(0)
    this.ctx?.save()
    if (this.ctx) {
      this.ctx.fillStyle = lineColor
    }
    while (x < this.width) {
      this.ctx?.fillRect(x, 0, 1, this.height)
      x += grid.dx
    }
    this.ctx?.restore()
  }

  gety(row: number) {
    return (grid.starty || grid.dy) + grid.dy * row
  }

  getx(row: number) {
    return (grid.startx || grid.dx) + grid.dx * row
  }

  getrcount() {
    return Math.ceil((this.height - this.gety(0)) / grid.dy)
  }

  getccount() {
    return Math.ceil((this.width - this.getx(0)) / grid.dx)
  }

  rand(min: number, max: number) {
    return min + Math.random() * (max - min)
  }
}
